import { z } from 'zod';
import { sha256HexSchema } from './canonicalJson';
import { macroInputModePolicySchema } from './inputModePolicy';
import { rawRegionRectSchema } from './rawPackage';

export const AUTHORING_PROFILE = 'input-minus-100ms-v1';
export const AUTHORING_REFERENCE_LEAD_US = 100_000;
export const AUTHORING_CALIBRATION_RADIUS_US = 100_000;
export const authoringTimeSchema = z.number().int().nonnegative().safe();
export const macroWorkspaceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('local'), workspaceId: z.string().uuid() }).strict(),
  z.object({ kind: z.literal('campaign'), campaignId: z.string().min(1).max(128) }).strict(),
]);
export type MacroWorkspace = z.infer<typeof macroWorkspaceSchema>;
export const recordingPinSchema = z.object({ packageId: z.string().uuid(), manifestSha256: sha256HexSchema }).strict();
export type RecordingPin = z.infer<typeof recordingPinSchema>;
export const recordingSpanSchema = z.object({ startUs: authoringTimeSchema, stopUs: authoringTimeSchema }).strict()
  .refine(span => span.stopUs > span.startUs, 'Recording span must be nonempty');
export type RecordingSpan = z.infer<typeof recordingSpanSchema>;
export const interactionTagIdSchema = z.string().regex(
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:(0|[1-9]\d*)$/i);
export const authoredFrameAnchorSchema = z.object({ frameSeq: authoringTimeSchema,
  tUs: authoringTimeSchema, ptsUs: authoringTimeSchema }).strict();
export type AuthoredFrameAnchor = z.infer<typeof authoredFrameAnchorSchema>;
export const authoredMatchRegionSchema = z.object({ regionId: z.string().uuid(), packageId: z.string().uuid(),
  tagId: interactionTagIdSchema, rect: rawRegionRectSchema,
  anchorFrame: authoredFrameAnchorSchema.optional(),
  matchThresholdPercent: z.number().int().min(0).max(100).optional(),
}).strict().refine(region => region.tagId.startsWith(`${region.packageId}:`), 'Region tag must belong to its recording');
export type AuthoredMatchRegion = z.infer<typeof authoredMatchRegionSchema>;
export const recordingArmSpecSchema = z.object({ workspace: macroWorkspaceSchema, name: z.string().min(1).max(80),
  captureSource: z.enum(['desktop', 'editor']), policy: macroInputModePolicySchema,
  sourcePin: z.object({ selectionId: z.string().min(1).max(256), fingerprint: sha256HexSchema }).strict(),
  referenceLeadUs: z.literal(AUTHORING_REFERENCE_LEAD_US), calibrationRadiusUs: z.literal(AUTHORING_CALIBRATION_RADIUS_US),
}).strict();
export type RecordingArmSpec = z.infer<typeof recordingArmSpecSchema>;
export const recordingLifecycleSchema = z.enum(['idle', 'warming', 'armed', 'recording', 'finalizing', 'finalized', 'failed']);
export type RecordingLifecycle = z.infer<typeof recordingLifecycleSchema>;
export const recordingLifecycleSnapshotSchema = z.object({ phase: recordingLifecycleSchema,
  transitionSeq: authoringTimeSchema, packageId: z.string().uuid().nullable(), span: recordingSpanSchema.optional(),
  failure: z.string().min(1).max(1024).optional(),
}).strict().superRefine((state, context) => {
  const fail = (message: string): void => context.addIssue({ code: z.ZodIssueCode.custom, message });
  if ((state.phase === 'idle') !== (state.packageId === null)) fail('Only idle may omit the recording identity');
  if ((state.phase === 'failed') !== (state.failure !== undefined)) fail('Failure details are required exactly when failed');
  if (['finalizing', 'finalized'].includes(state.phase) && !state.span) fail('Stopped recording requires its sealed span');
  if (['idle', 'warming', 'armed', 'recording'].includes(state.phase) && state.span) fail('Unstopped recording cannot claim a sealed span');
});
export type RecordingLifecycleSnapshot = z.infer<typeof recordingLifecycleSnapshotSchema>;
export const recordingCopyStatusSchema = z.object({ location: z.enum(['host', 'author']),
  state: z.enum(['pending', 'copying', 'verified', 'failed']), bytesDone: authoringTimeSchema, bytesTotal: authoringTimeSchema,
  pin: recordingPinSchema, error: z.string().min(1).max(1024).optional(),
}).strict().superRefine((copy, context) => {
  if (copy.bytesDone > copy.bytesTotal || (copy.state === 'verified' && copy.bytesDone !== copy.bytesTotal)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Copy progress must reconcile before verification' });
  }
  if ((copy.state === 'failed') !== (copy.error !== undefined)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Failed copy requires an error; other states cannot claim one' });
  }
});
export type RecordingCopyStatus = z.infer<typeof recordingCopyStatusSchema>;
